/**
 * THE CONSOLE'S MAP — every screen, in one place.
 *
 * The rail, the router, the breadcrumb and the keyboard jump list all read this
 * array. Four readers of one fact, so the fact lives here: a section added to
 * the rail but forgotten in the router is a dead link, and a section routed but
 * missing from the rail is a screen nobody finds.
 *
 * ── THE GROUPING IS THE INFORMATION ARCHITECTURE ───────────────────────────
 *
 * Not alphabetical, and not "tables we happen to have". Four groups, ordered by
 * the question an operator is holding when they open the console:
 *
 *   Platform    how is the business doing, and who is in it
 *   Property    the physical estate, top down: portfolio → building → unit → resident
 *   Commercial  what people pay and what they consume
 *   Operations  what the product is actually doing, hour to hour
 *   System      is the machine healthy, and what have WE done to it
 */

export interface Section {
  /** The URL segment under /admin. '' is the overview. */
  path: string;
  label: string;
  group: 'Platform' | 'Property' | 'Commercial' | 'Operations' | 'System';
  /** Single-letter accelerator, pressed after `g`. Unique across the console. */
  key?: string;
  /** Shown under the page title. One line, plain language. */
  blurb?: string;
}

export const SECTIONS: Section[] = [
  { path: '', label: 'Overview', group: 'Platform', key: 'o',
    blurb: 'The whole platform on one screen. Every number names its source.' },
  { path: 'organizations', label: 'Organizations', group: 'Platform', key: 'c',
    blurb: 'Every customer account, its people, its estate and its subscription.' },
  { path: 'users', label: 'People', group: 'Platform', key: 'u',
    blurb: 'Every account on the platform. Search any fragment of a name, address or phone.' },
  { path: 'admins', label: 'Platform admins', group: 'Platform',
    blurb: 'Who can see this console, and who granted them.' },

  { path: 'portfolios', label: 'Portfolios', group: 'Property', key: 'f',
    blurb: 'The layer between an organization and its buildings, and who owns each one.' },
  { path: 'buildings', label: 'Buildings', group: 'Property', key: 'b' },
  { path: 'units', label: 'Units', group: 'Property', key: 'n' },
  { path: 'residents', label: 'Residents', group: 'Property', key: 'r',
    blurb: 'Occupancies across every building. Tenancy facts only — no visitor history, no parcels.' },

  { path: 'subscriptions', label: 'Subscriptions', group: 'Commercial', key: 's' },
  { path: 'usage', label: 'Usage', group: 'Commercial', key: 'g',
    blurb: 'Units bought against units consumed, and what the AI layer costs.' },
  { path: 'catalogues', label: 'Plans & catalogues', group: 'Commercial' },
  { path: 'promos', label: 'Promotions', group: 'Commercial', key: 'm',
    blurb: 'Discount codes: what they take off, where they apply, how long they last and how many are left.' },
  { path: 'redemptions', label: 'Redemptions', group: 'Commercial',
    blurb: 'Who used which code, on which subscription, and whether it ever reached a paid invoice.' },
  { path: 'waitlist', label: 'Waitlists', group: 'Commercial', key: 'w',
    blurb: 'Who is waiting for a product that has not shipped. Addresses are stored encrypted and decrypted here for display.' },
  { path: 'stays-billing', label: 'Stays billing', group: 'Commercial',
    blurb: 'Stays-only hosts on a plan, listing slots and the per-check-in meter. Counts only, never a guest.' },
  { path: 'stripe', label: 'Stripe events', group: 'Commercial',
    blurb: 'The webhook stream. Payloads are deliberately not shown.' },

  { path: 'invitations', label: 'Invitations', group: 'Operations', key: 'i' },
  { path: 'logs', label: 'Log entries', group: 'Operations', key: 'l',
    blurb: 'Visitor entries and Building Activities. Shape only — never the free text.' },
  { path: 'packages', label: 'Parcels', group: 'Operations', key: 'p' },
  { path: 'visitor-passes', label: 'Visitor passes', group: 'Operations',
    blurb: 'Standing access registered for visitors, across every customer. Shape only — never a name, a note or the resident.' },
  { path: 'stays-reservations', label: 'Short stays', group: 'Operations',
    blurb: 'Short stays booked across every customer. Shape only — never a guest name, an email or a note.' },
  { path: 'stays-signups', label: 'Stays sign-ups', group: 'Operations',
    blurb: 'Every account the public Lobbero Stays sign-up made, and how far each has got.' },
  { path: 'stays-accounts', label: 'Independent accounts', group: 'Operations',
    blurb: 'Accounts with Lobbero Stays and no Lobbero Desk: products, plan, activity and whether they are closing.' },
  { path: 'stays-closures', label: 'Closures', group: 'Operations',
    blurb: 'Accounts their owners closed: frozen, reopened, removed or failed. A failed removal can be retried.' },
  { path: 'shifts', label: 'Shifts', group: 'Operations' },
  { path: 'access', label: 'Access grants', group: 'Operations', key: 'a' },
  { path: 'sessions', label: 'Sessions', group: 'Operations' },
  { path: 'notifications', label: 'Notifications', group: 'Operations' },
  { path: 'iq', label: 'Lobbero IQ', group: 'Operations', key: 'q' },

  { path: 'server', label: 'Server', group: 'System', key: 'v',
    blurb: 'The box itself: memory, swap, CPU, disk, services, certificates, the backup and the database — with one verdict.' },
  { path: 'system-logs', label: 'System logs', group: 'System', key: 'x',
    blurb: 'Every line the box writes — the app, the proxy, the backup, SSH, the kernel. Filter, search, follow live.' },
  { path: 'backups', label: 'Backups', group: 'System', key: 'k',
    blurb: 'Every database backup that exists — when it was taken, why, and whether it is on the box, in S3, or both.' },
  { path: 'performance', label: 'Performance', group: 'System', key: 'e',
    blurb: 'This process: request rates, latency percentiles, error rate, event-loop lag.' },
  { path: 'database', label: 'Database', group: 'System', key: 'd',
    blurb: 'PostgreSQL answering for itself. Statement text is deliberately withheld.' },
  { path: 'health', label: 'Health & integrations', group: 'System', key: 'h',
    blurb: 'Every outside service — email, address search, the map, Stripe, Gemini, push, image storage, PDFs: working or not, and what each one last said.' },
  { path: 'integrity', label: 'Data integrity', group: 'System' },
  { path: 'storage', label: 'Storage', group: 'System' },
  { path: 'credentials', label: 'Credentials in flight', group: 'System' },
  { path: 'audit', label: 'Audit trail', group: 'System', key: 'y',
    blurb: 'Every privileged action taken from this console. Append-only.' },
];

export const GROUPS: Section['group'][] = [
  'Platform',
  'Property',
  'Commercial',
  'Operations',
  'System',
];

export const sectionFor = (path: string, sections: Section[] = SECTIONS): Section | undefined =>
  sections.find(s => s.path === path);

/** IS8.0 (IS8-D4): the sections this console shows — the Lobbero Stays ones
 *  (path 'stays-…') only while Stays is switched on on the server. */
export const isStaysSection = (s: Section): boolean => s.path.startsWith('stays-');
export const visibleSections = (staysOn: boolean): Section[] =>
  staysOn ? SECTIONS : SECTIONS.filter(s => !isStaysSection(s));
